import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import Fab from '@mui/material/Fab'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import colors from '@/assets/scss/_themes-vars.module.scss'

import { ChatInterface } from '@/features/medconnect-agent/ui/ChatInterface'
import { useElaraPanel } from '@/providers/elara/ElaraPanelContext'
import { useGetAllSessionsQuery } from '@/core/services/medconnect-agent-service'
import { useElaraChatEnabled } from '@/providers/elara/hooks/useElaraChatEnabled'

export const ELARA_PANEL_WIDTH = 420

/**
 * Hides the third-party support launcher (Freshworks `iframe#launcher-frame`, the bottom-right
 * "ASSISTANCE" pill) while `active` is true. Uses the official widget API when available and
 * falls back to hiding the iframe directly. Restores the launcher on cleanup.
 */
export function useHideSupportLauncher(active: boolean) {
    useEffect(() => {
        if (!active) return
        const hide = () => {
            const fw = (window as any).FreshworksWidget
            if (typeof fw === 'function') fw('hide', 'launcher')
            const frame = document.getElementById('launcher-frame')
            if (frame) frame.style.setProperty('display', 'none', 'important')
            return Boolean(frame)
        }
        // The widget script loads async — retry briefly until the launcher exists.
        let tries = 0
        const interval = window.setInterval(() => {
            if (hide() || ++tries >= 20) window.clearInterval(interval)
        }, 500)
        hide()
        return () => {
            window.clearInterval(interval)
            const fw = (window as any).FreshworksWidget
            if (typeof fw === 'function') fw('show', 'launcher')
            const f = document.getElementById('launcher-frame')
            if (f) f.style.removeProperty('display')
        }
    }, [active])
}

/**
 * FAB button — toggles the ELARA panel.
 * Only rendered when the ELARA chat is available (elara-realtime licence or rollout flag).
 *
 * Mobile (<sm): smaller, stacked above the Freshworks "ASSISTANCE" launcher
 * (46px visible height at the bottom-right) with a ≥12px gap, lifted above the
 * iOS home-indicator safe area. Hidden on the full-page ELARA chat where it is redundant.
 */
export function ElaraPanelFAB() {
    const { t } = useTranslation()
    const panel = useElaraPanel()
    const canUseElaraChat = useElaraChatEnabled()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const { pathname } = useLocation()
    if (!panel || panel.isPanelOpen || !canUseElaraChat) return null
    // The full-page chat *is* ELARA — a FAB there only covers the input bar.
    if (pathname.startsWith('/medconnect-agent')) return null

    return (
        <Tooltip title={t('page.elara.panel.openTooltip')} placement="left">
            <Fab
                size={isMobile ? 'small' : 'medium'}
                onClick={panel.togglePanel}
                sx={{
                    position: 'fixed',
                    // Mobile: clear the Freshworks launcher (46px visible) + 12px gap + safe area.
                    bottom: { xs: 'calc(58px + env(safe-area-inset-bottom))', sm: 24 },
                    right: { xs: 16, sm: 24 },
                    zIndex: 1300,
                    backgroundColor: colors.primaryMain,
                    color: '#fff',
                    '&:hover': { backgroundColor: colors.primaryDark },
                }}
            >
                <SmartToyIcon />
            </Fab>
        </Tooltip>
    )
}

/**
 * Conversation title bar with session history dropdown.
 * Shows current conversation title (like Cloudflare's "Where do I find my ac... ▾").
 */
function ConversationTitleBar({
    sessionId,
    onSessionSelect,
    onNewChat,
}: {
    sessionId: string | null
    onSessionSelect: (id: string) => void
    onNewChat: () => void
}) {
    const { t } = useTranslation()
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    // Same shared RTK sessions list as /medconnect-agent; refetch on mount so page-created
    // sessions appear in the panel picker without a reload.
    const { data: sessions, isLoading } = useGetAllSessionsQuery(undefined, { refetchOnMountOrArgChange: true })

    const currentSession = useMemo(
        () => sessions?.find((s) => s.sessionId === sessionId),
        [sessions, sessionId],
    )

    const title = currentSession?.title || t('page.elara.panel.newConversation', { defaultValue: 'New conversation' })

    const handleOpen = useCallback((e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget), [])
    const handleClose = useCallback(() => setAnchorEl(null), [])

    const handleSelect = useCallback(
        (id: string) => {
            onSessionSelect(id)
            handleClose()
        },
        [onSessionSelect, handleClose],
    )

    const handleNewChat = useCallback(() => {
        onNewChat()
        handleClose()
    }, [onNewChat, handleClose])

    const recentSessions = useMemo(
        () => (sessions || []).slice(0, 20),
        [sessions],
    )

    return (
        <>
            <ButtonBase
                onClick={handleOpen}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textAlign: 'left',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                    flex: 1,
                    minWidth: 0,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
            >
                <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 500, color: colors.gray800 || '#1a1a1a', flex: 1, minWidth: 0 }}
                >
                    {title}
                </Typography>
                <ExpandMoreIcon sx={{ fontSize: 18, color: colors.gray500, flexShrink: 0 }} />
            </ButtonBase>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: {
                            width: ELARA_PANEL_WIDTH - 16,
                            maxHeight: 360,
                            mt: 0.5,
                            borderRadius: 2,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                        },
                    },
                }}
            >
                {/* New conversation at the top */}
                <ListItemButton onClick={handleNewChat} sx={{ py: 1 }}>
                    <AddIcon sx={{ fontSize: 18, mr: 1, color: colors.primaryMain }} />
                    <ListItemText
                        primary={t('page.elara.panel.newConversation', { defaultValue: 'New conversation' })}
                        primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500, color: colors.primaryMain }}
                    />
                </ListItemButton>
                <Divider />

                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress size={20} />
                    </Box>
                ) : recentSessions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 2, textAlign: 'center' }}>
                        {t('page.elara.panel.noConversations', { defaultValue: 'No previous conversations' })}
                    </Typography>
                ) : (
                    <List dense sx={{ py: 0, maxHeight: 280, overflow: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                        {recentSessions.map((session) => (
                            <ListItemButton
                                key={session.sessionId}
                                selected={session.sessionId === sessionId}
                                onClick={() => handleSelect(session.sessionId)}
                                sx={{ py: 0.75 }}
                            >
                                <ListItemText
                                    primary={session.title || session.firstMessagePreview || t('page.elara.panel.untitled')}
                                    primaryTypographyProps={{ fontSize: '0.825rem', noWrap: true }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    )
}

/**
 * ELARA side panel — Cloudflare-style.
 */
export function ElaraPanelContent() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const theme = useTheme()
    // Below md the panel cannot share layout space (it would crush the page) — full-screen overlay.
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const panel = useElaraPanel()
    const [refreshTick, setRefreshTick] = useState(0)

    const closePanelRef = useRef(panel?.closePanel)
    closePanelRef.current = panel?.closePanel

    // Mobile: any in-app navigation (e.g. tapping an "Open consultation" link in a reply)
    // must close the overlay — otherwise it keeps covering the destination page.
    const prevPathnameRef = useRef(location.pathname)
    useEffect(() => {
        if (location.pathname !== prevPathnameRef.current) {
            prevPathnameRef.current = location.pathname
            if (isMobile) closePanelRef.current?.()
        }
    }, [location.pathname, isMobile])

    // Mobile overlay: lock body scroll and hide the Freshworks launcher underneath.
    useEffect(() => {
        if (!isMobile) return
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isMobile])
    useHideSupportLauncher(isMobile)

    if (!panel) return null

    const { sessionId, setSessionId, closePanel, newChat, pendingPrompt, pendingFiles, pendingPromptNonce, clearPendingPrompt } = panel

    const handleSessionIdChange = useCallback(
        (id: string) => setSessionId(id),
        [setSessionId],
    )

    const handleRefresh = useCallback(() => setRefreshTick((n) => n + 1), [])

    const handleNewChat = useCallback(() => {
        setRefreshTick(0)
        newChat()
    }, [newChat])

    const handleOpenFullPage = useCallback(() => {
        const target = sessionId ? `/medconnect-agent/${sessionId}` : '/medconnect-agent'
        navigate(target)
        closePanel()
    }, [navigate, closePanel, sessionId])

    return (
        <Stack
            sx={{
                // Mobile (<md): full-screen fixed overlay — never shares layout space with the page.
                width: { xs: '100%', md: ELARA_PANEL_WIDTH },
                minWidth: { xs: 0, md: ELARA_PANEL_WIDTH },
                maxWidth: { xs: '100%', md: ELARA_PANEL_WIDTH },
                // 100dvh tracks the *visible* viewport on mobile browsers (URL bar); 100vh is the fallback.
                height: '100vh',
                '@supports (height: 100dvh)': { height: '100dvh' },
                position: { xs: 'fixed', md: 'sticky' },
                top: 0,
                right: 0,
                left: { xs: 0, md: 'auto' },
                zIndex: { xs: theme.zIndex.modal, md: 'auto' },
                borderLeft: { xs: 'none', md: `1px solid ${colors.gray200}` },
                backgroundColor: '#fff',
                overflow: 'hidden',
                flexShrink: 0,
                pb: { xs: 'env(safe-area-inset-bottom)', md: 0 },
            }}
        >
            {/* Top bar — conversation title + actions, single row */}
            <Stack
                direction="row"
                alignItems="center"
                sx={{
                    px: 1,
                    minHeight: 46,
                    flexShrink: 0,
                    borderBottom: `1px solid ${colors.gray200}`,
                    gap: 0.5,
                }}
            >
                <ConversationTitleBar
                    sessionId={sessionId}
                    onSessionSelect={setSessionId}
                    onNewChat={handleNewChat}
                />
                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                    <Tooltip title={t('page.elara.panel.refreshHistory', { defaultValue: 'Reload history' })}>
                        <span>
                            <IconButton size="small" onClick={handleRefresh} disabled={!sessionId} sx={{ color: colors.gray500 }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={t('page.elara.panel.newChat', { defaultValue: 'New conversation' })}>
                        <IconButton size="small" onClick={handleNewChat} sx={{ color: colors.gray500 }}>
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('page.elara.panel.openFullPage', { defaultValue: 'Open in full page' })}>
                        <IconButton size="small" onClick={handleOpenFullPage} sx={{ color: colors.gray500 }}>
                            <OpenInFullIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('page.elara.panel.closeTooltip')}>
                        <IconButton size="small" onClick={closePanel} sx={{ color: colors.gray500 }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {/* Chat area — scroll-isolated */}
            <Box
                sx={{
                    flex: 1,
                    overflow: 'hidden',
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overscrollBehavior: 'contain',
                }}
            >
                {/* Keyed on the askElara nonce: a new question from outside remounts on a fresh conversation.
                    The prompt is cleared once sent so reopening the panel never re-sends it. */}
                <ChatInterface
                    key={pendingPromptNonce}
                    sessionId={sessionId}
                    initialPrompt={pendingPrompt ?? undefined}
                    initialFiles={pendingFiles}
                    onMessageSent={clearPendingPrompt}
                    onSessionIdChange={handleSessionIdChange}
                    compact
                    refreshTick={refreshTick}
                />
            </Box>
        </Stack>
    )
}

/**
 * Legacy export for App.tsx — just renders the FAB.
 */
export function ElaraPanel() {
    return <ElaraPanelFAB />
}
